import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { ExternalLink, MapPin, Building2, DollarSign, TrendingUp, Calendar, FileText, Camera } from "lucide-react";
import { useState } from "react";
import { TYPE_BY_VALUE, benchmarkFor } from "../lib/propertyTypes";
import PropertyImage from "./PropertyImage";
import { RetakePhotoDialog } from "./RetakePhotoDialog";

const TYPE_LABEL = Object.fromEntries(
  Object.entries(TYPE_BY_VALUE).map(([value, meta]) => [value, meta.label]),
);

function money(n) {
  return "$" + (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function yearsHeldExact(isoDate) {
  if (!isoDate) return null;
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return null;
  const yrs = (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24 * 365.25);
  return yrs >= 0 ? yrs : null;
}

function yearsHeld(isoDate) {
  const y = yearsHeldExact(isoDate);
  if (y == null) return null;
  if (y < 1) return Math.max(0, Math.round(y * 10) / 10);
  return Math.floor(y);
}

/**
 * Read-only "view" dialog for a single property. Shows hero image, address
 * with Google Maps deep-link, KPI tiles (units, years-held, purchase/market,
 * annual taxes/insurance, appreciation, cap-rate vs benchmark, NOI,
 * coordinates), notes, and footer actions (Public Listing link, Edit, Close).
 */
export function PropertyViewDialog({ property, onClose, onEdit }) {
  const p = property;
  const open = !!p;
  const [retakeOpen, setRetakeOpen] = useState(false);
  const [imgVersion, setImgVersion] = useState(0);
  if (!p) return (
    <Dialog open={false} onOpenChange={() => {}}>
      <DialogContent />
    </Dialog>
  );

  const purchase = Number(p.purchase_price) || 0;
  const market = Number(p.market_value) || 0;
  const gain = market - purchase;
  const gainPct = purchase > 0 ? (gain / purchase) * 100 : null;
  const yrs = yearsHeld(p.purchase_date);
  const gainColor = gain > 0 ? "text-emerald-600" : gain < 0 ? "text-red-600" : "text-slate-500";
  const typeMeta = TYPE_BY_VALUE[p.property_type];
  const cap = p.cap_rate;
  const noi = Number(p.noi) || 0;
  const bench = typeof cap === "number" ? benchmarkFor(p.property_type, cap) : { status: "unknown" };
  const benchTone = bench.status === "below" ? "text-red-600" : bench.status === "above" ? "text-emerald-600" : "text-cyan-700";

  const mapsUrl = p.lat != null && p.lng != null
    ? `https://www.google.com/maps/search/?api=1&query=${p.lat},${p.lng}`
    : p.formatted_address || p.address
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(p.formatted_address || p.address)}`
      : null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="property-view-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }} className="flex items-center gap-2 text-slate-900">
            <span className="text-xl">{typeMeta?.emoji || "📦"}</span>
            <span data-testid="property-view-name">{p.name}</span>
            <span className="overline text-slate-500 ml-2">{TYPE_LABEL[p.property_type] || p.property_type}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="relative w-full h-56 bg-slate-100 rounded-sm overflow-hidden group">
            <PropertyImage pid={p.id} size="640x320" className="w-full h-56" rounded="rounded-sm" alt={p.name} refreshKey={imgVersion} />
            <button
              type="button"
              onClick={() => setRetakeOpen(true)}
              className="absolute top-2 right-2 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm bg-slate-900/85 hover:bg-slate-900 text-white text-xs font-mono uppercase tracking-widest backdrop-blur-sm border border-white/10 shadow-sm transition"
              data-testid="property-view-retake-photo"
            >
              <Camera className="w-3.5 h-3.5" />
              Retake photo
            </button>
          </div>

          <div className="flex items-start gap-2 text-sm text-slate-700">
            <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-slate-400" />
            <div className="flex-1">
              <div data-testid="property-view-address">{p.formatted_address || p.address || "—"}</div>
              {mapsUrl && (
                <a
                  href={mapsUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs font-mono uppercase tracking-widest text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 mt-1"
                  data-testid="property-view-maps-link"
                >
                  Open in Google Maps <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="property-view-kpis">
            <ViewTile icon={<Building2 className="w-3 h-3" />} label="Units" value={p.units ?? "—"} />
            <ViewTile icon={<Calendar className="w-3 h-3" />} label="Years held" value={yrs != null ? `${yrs} ${yrs === 1 ? "yr" : "yrs"}` : "—"} sub={p.purchase_date || ""} />
            <ViewTile icon={<DollarSign className="w-3 h-3" />} label="Purchase price" value={money(purchase)} />
            <ViewTile icon={<DollarSign className="w-3 h-3" />} label="Market value" value={money(market)} />
            <ViewTile icon={<DollarSign className="w-3 h-3" />} label="Annual taxes" value={money(Number(p.annual_taxes) || 0)} />
            <ViewTile icon={<DollarSign className="w-3 h-3" />} label="Annual insurance" value={money(Number(p.annual_insurance) || 0)} />
            <ViewTile
              icon={<TrendingUp className="w-3 h-3" />}
              label="Appreciation"
              value={purchase > 0 ? `${gain >= 0 ? "+" : "−"}${money(Math.abs(gain))}` : "—"}
              sub={gainPct != null ? `${gain >= 0 ? "▲" : "▼"} ${gainPct.toFixed(1)}%` : ""}
              tone={gainColor}
            />
            <ViewTile
              icon={<TrendingUp className="w-3 h-3" />}
              label="Cap rate"
              value={cap != null ? `${cap.toFixed(2)}%` : "—"}
              sub={bench.min != null ? `Typical ${bench.min}–${bench.max}%` : ""}
              tone={benchTone}
            />
            <ViewTile icon={<DollarSign className="w-3 h-3" />} label="Annual NOI" value={money(noi)} />
            <ViewTile
              icon={<MapPin className="w-3 h-3" />}
              label="Coordinates"
              value={p.lat != null && p.lng != null ? `${Number(p.lat).toFixed(4)}, ${Number(p.lng).toFixed(4)}` : "—"}
              sub={p.place_id ? "Verified by Google" : ""}
            />
          </div>

          {p.notes && (
            <div className="rounded-sm border border-slate-200 bg-slate-50 p-3 flex items-start gap-2 text-sm text-slate-700" data-testid="property-view-notes">
              <FileText className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
              <span className="whitespace-pre-wrap">{p.notes}</span>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <a
            href={`/listings/${p.id}`}
            target="_blank"
            rel="noreferrer"
            className="btn-outline-dark inline-flex items-center gap-1 text-sm"
            data-testid="property-view-public-link"
          >
            Public listing <ExternalLink className="w-3.5 h-3.5" />
          </a>
          <button type="button" className="btn-outline-dark" onClick={onClose} data-testid="property-view-close">Close</button>
          <button type="button" className="btn-primary-dark" onClick={() => onEdit(p)} data-testid="property-view-edit">Edit</button>
        </DialogFooter>

        <RetakePhotoDialog
          property={retakeOpen ? p : null}
          open={retakeOpen}
          onClose={() => setRetakeOpen(false)}
          onSaved={() => setImgVersion((v) => v + 1)}
        />
      </DialogContent>
    </Dialog>
  );
}

function ViewTile({ icon, label, value, sub, tone }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-3">
      <div className="overline text-slate-500 flex items-center gap-1">{icon}{label}</div>
      <div className={`mt-1 font-mono font-bold text-sm ${tone || "text-slate-900"}`}>{value}</div>
      {sub && <div className={`text-[10px] font-mono uppercase tracking-widest mt-0.5 ${tone ? tone : "text-slate-500"}`}>{sub}</div>}
    </div>
  );
}
